/**
 * Event Emitters for NodeTemplates System
 * Dispatches CustomEvents on the window object for extensibility
 */

import { NodeTemplateEventDetail, EntityType, EVENT_NAMES } from './types';
import type { NodeType } from '../Domain/NodeType';
import type { NodeCategory } from '../Domain/NodeCategory';
import type { NodeTemplate } from '../Domain/NodeTemplate';

/**
 * Base event emitter function
 */
function emitEvent<T>(eventName: string, detail: NodeTemplateEventDetail<T>): void {
  const event = new CustomEvent(eventName, { detail });
  window.dispatchEvent(event);
}

/**
 * Emit fetch started event
 */
export function emitFetchStarted(entityType: EntityType): void {
  const eventName =
    entityType === 'NodeType' ? EVENT_NAMES.NODE_TYPES_FETCH_STARTED :
    entityType === 'NodeCategory' ? EVENT_NAMES.NODE_CATEGORIES_FETCH_STARTED :
    EVENT_NAMES.TEMPLATES_FETCH_STARTED;

  emitEvent(eventName, {
    entityType,
    action: 'FetchStarted',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Emit fetch completed event
 */
export function emitFetchCompleted<T>(
  entityType: EntityType,
  data: T
): void {
  const eventName =
    entityType === 'NodeType' ? EVENT_NAMES.NODE_TYPES_FETCH_COMPLETED :
    entityType === 'NodeCategory' ? EVENT_NAMES.NODE_CATEGORIES_FETCH_COMPLETED :
    EVENT_NAMES.TEMPLATES_FETCH_COMPLETED;

  emitEvent(eventName, {
    entityType,
    action: 'FetchCompleted',
    data,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Emit fetch failed event
 */
export function emitFetchFailed(entityType: EntityType, error: Error): void {
  const eventName =
    entityType === 'NodeType' ? EVENT_NAMES.NODE_TYPES_FETCH_FAILED :
    entityType === 'NodeCategory' ? EVENT_NAMES.NODE_CATEGORIES_FETCH_FAILED :
    EVENT_NAMES.TEMPLATES_FETCH_FAILED;

  emitEvent(eventName, {
    entityType,
    action: 'FetchFailed',
    error,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Emit enhancement started event
 */
export function emitEnhancementStarted(): void {
  emitEvent(EVENT_NAMES.TEMPLATES_ENHANCEMENT_STARTED, {
    entityType: 'NodeTemplate',
    action: 'EnhancementStarted',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Emit enhancement completed event
 */
export function emitEnhancementCompleted(templates: NodeTemplate[]): void {
  emitEvent(EVENT_NAMES.TEMPLATES_ENHANCEMENT_COMPLETED, {
    entityType: 'NodeTemplate',
    action: 'EnhancementCompleted',
    data: templates,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Emit ready event
 * This is the final event indicating all templates are loaded and enhanced
 */
export function emitReady(data: {
  templates: NodeTemplate[];
  nodeTypes: NodeType[];
  nodeCategories: NodeCategory[];
}): void {
  emitEvent(EVENT_NAMES.TEMPLATES_READY, {
    entityType: 'NodeTemplate',
    action: 'Ready',
    data,
    timestamp: new Date().toISOString(),
  });
}
